import PropTypes from 'prop-types';
import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath } from '@xyflow/react';
import { PlusIcon } from '@heroicons/react/24/solid';
import { useState } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useDismiss,
  useInteractions,
  safePolygon,
} from '@floating-ui/react';

const SuggestedEdge = props => {
  const { sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style, markerEnd } = props;

  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const {
    refs: tooltipRefs,
    floatingStyles: tooltipStyles,
    context: tooltipContext,
  } = useFloating({
    open: isTooltipOpen,
    onOpenChange: setIsTooltipOpen,
    placement: 'top',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      flip({
        fallbackAxisSideDirection: 'start',
      }),
      shift(),
    ],
  });

  const tooltipHover = useHover(tooltipContext, { handleClose: safePolygon() });
  const tooltipDismiss = useDismiss(tooltipContext);
  const { getReferenceProps: getTooltipReferenceProps, getFloatingProps: getTooltipFloatingProps } = useInteractions([
    tooltipHover,
    tooltipDismiss,
  ]);

  const indicatorStyle = {
    transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
    pointerEvents: 'all',
  };

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
      <EdgeLabelRenderer>
        <div
          ref={tooltipRefs.setReference}
          {...getTooltipReferenceProps()}
          // @ts-ignore - pointerEvents is valid CSS property
          style={indicatorStyle}
          className="z-1000 absolute pointer nodrag nopan"
        >
          <div
            className={`flex items-center justify-center w-8 h-8 rounded-full bg-green-300 outline-4 outline outline-green-200 shadow-md hover:scale-110 transition-transform cursor-pointer`}
          >
            <PlusIcon className="w-5 h-5 text-white" />
          </div>
          {isTooltipOpen && (
            <div
              ref={tooltipRefs.setFloating}
              style={tooltipStyles}
              {...getTooltipFloatingProps()}
              className="z-1000 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-lg text-gray-600 w-56"
            >
              <b>Suggested connection:</b> click to confirm, form new connection to ignore.
            </div>
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

SuggestedEdge.propTypes = {
  id: PropTypes.string,
  sourceX: PropTypes.number,
  sourceY: PropTypes.number,
  targetX: PropTypes.number,
  targetY: PropTypes.number,
  sourcePosition: PropTypes.string,
  targetPosition: PropTypes.string,
  style: PropTypes.object,
  markerEnd: PropTypes.string,
  data: PropTypes.object,
};

export default SuggestedEdge;
